/**
 * Dizionario pt (portoghese europeu).
 *
 * Portogallo è già nel registro dei paesi e `LOCALE_BY_COUNTRY` suggeriva `pt`
 * senza che il dizionario lo avesse: `/api/geo` rispondeva `supported: false` e
 * un host di Lisbona vedeva l'interfaccia inglese.
 *
 * Le chiavi devono essere identiche a quelle di `it`: il test
 * `backend/tests/test_jurisdictions.py::test_le_lingue_dichiarate_sono_quelle_tradotte`
 * confronta i file di questa cartella con `APP_LOCALES`, e
 * `test_b2b_frontend_wiring.py` controlla che ogni chiave usata da un pannello
 * esista in **ogni** lingua.
 */
export default {
  "nav": {
    "showcase": "Montra",
    "directory": "Alojamentos",
    "network": "Rede",
    "pricing": "Tarifas",
    "login": "Entrar",
    "signup": "Registar alojamento",
    "dashboard": "Painel",
    "logout": "Sair",
    "book": "Reservar",
    "host_area": "Gerir um alojamento",
    "find_stay": "Encontrar alojamento",
    "bedbank": "Bedbank",
    "claim": "Reclamar",
    "affiliates": "Afiliados",
    "merchant_area": "Área do comércio",
    "merchants": "Comércios",
    "merchant_login": "Acesso comércios",
    "merchant_login_title": "Tenho um negócio local",
    "merchant_space": "Espaço de comércio",
    "host_dashboard": "Painel do anfitrião",
    "account": "Conta",
    "language": "Idioma"
  },
  "landing": {
    "eyebrow": "Plataforma para alojamentos",
    "title1": "Hospitalidade italiana,",
    "title2": "no mundo.",
    "subtitle": "Divulgue o seu alojamento, sincronize as OTAs, troque quartos com parceiros de confiança. Uma casa digital para B&Bs, casas de campo e hotéis.",
    "cta_primary": "Começar grátis",
    "cta_secondary": "Ver montras",
    "f1_title": "Uma montra em sete línguas",
    "f1_body": "Uma página pública em italiano, inglês, espanhol, francês, alemão, português e grego que conta o seu alojamento. Fotos, quartos, reserva direta com Stripe.",
    "f2_title": "Rede entre alojamentos",
    "f2_body": "Overbooking? Encaminhe o cliente para um parceiro. Mercado nacional + grupos fechados locais.",
    "f3_title": "Pedidos de grupo",
    "f3_body": "Publique um pedido de grupo grande e os alojamentos próximos respondem. Pequeno-almoço centralizado incluído.",
    "f4_title": "Calendário único",
    "f4_body": "Reservas diretas, quartos em rede, OTAs sincronizadas por iCal (a caminho). Zero overbooking.",
    "f5_title": "PMS incluído",
    "f5_body": "Um verdadeiro Property Management System dentro da plataforma: front office, receção, limpeza, cozinha, compras e registo de operações. Sem software extra para pagar.",
    "showcase_title": "Alojamentos em montra",
    "showcase_sub": "Algumas das casas que se contam na Hostaly.",
    "how_title": "Como funciona",
    "how_1": "Registe o seu alojamento",
    "how_1b": "Registo em 60 segundos. Verificação automática para o MVP.",
    "how_2": "Configure a montra em sete línguas",
    "how_2b": "Textos em cada língua, fotos, serviços, quartos. Publique quando estiver pronto.",
    "how_3": "Entre na rede",
    "how_3b": "Ofereça quartos de última hora, responda a pedidos de grupo, receba pagamentos.",
    "final_title": "A casa digital dos alojamentos",
    "final_body": "Uma única plataforma para gerir montra, rede e reservas."
  },
  "auth": {
    "login": "Entrar",
    "register": "Registar o seu alojamento",
    "email": "E-mail",
    "password": "Palavra-passe",
    "structure": "Nome do alojamento",
    "country": "País do alojamento",
    "country_help": "Decide as obrigações que lhe dizem respeito. Não é o idioma da interface: esse escolhe-se à parte.",
    "cin_label": "Código CIN",
    "cin_help": "Código de Identificação Nacional MITUR — obrigatório por lei (DL 145/2023). Formato nacional: IT + 15 caracteres (17 no total). Algumas regiões como a Sicília podem usar formatos mais longos (até 22 caracteres). Peça-o em bdsr.ministeroturismo.gov.it",
    "cin_not_required": "Fora de Itália o CIN não é necessário: pediremos o número de registo local quando for altura.",
    "submit_login": "Entrar",
    "submit_register": "Criar conta",
    "aff_rules": "Regras de afiliação",
    "aff_consent": "Declaro que li e aceitei as",
    "aff_consent_hint": "Obrigatório para registar o alojamento: inclui comissões, pagamentos, obrigações do alojamento e todas as promoções previstas no contrato (cláusula 6.4).",
    "switch_to_register": "Não tem conta? Registe o alojamento",
    "switch_to_login": "Já tem conta? Entre",
    "forgot": "Esqueceu-se da palavra-passe?",
    "demo_hint": "Demo: demo@hospitalityhub.it / demo1234",
    "staff_title": "Crie a sua conta",
    "staff_hint": "O titular do alojamento atribuiu-lhe um perfil. Use exatamente o endereço de email com que o atribuiu: é esse que liga a sua conta ao perfil.",
    "staff_name": "Nome completo",
    "staff_name_help": "Opcional. Serve apenas para os colegas o reconhecerem.",
    "role_label": "O seu perfil no alojamento",
    "role_general_manager": "General Manager (proprietário)",
    "role_operations_manager": "Operations Manager",
    "role_manager": "Manager",
    "role_operator": "Operador (limpeza/cozinha/reparações)",
    "role_help": "Determina o que pode fazer na Gestão Hoteleira. O General Manager gere os perfis da equipa.",
    "staff_password_hint": "Pelo menos 8 caracteres.",
    "staff_submit": "Criar a minha conta",
    "account_created": "Conta criada",
    "welcome_back": "Bem-vindo(a) de volta",
    "cin_invalid": "Código CIN inválido. Deve começar por 'IT' seguido de 14-20 caracteres alfanuméricos.",
    "cin_title": "Começa por IT + 14-20 caracteres (16-22 no total, ex. Sicília 18)",
    "aff_required": "Tem de ler as Regras de afiliação para continuar.",
    "google": "Continuar com Google",
    "apple": "Continuar com Apple",
    "soon": "brevemente",
    "google_unavailable": "Início de sessão com Google indisponível",
    "google_not_configured": "O acesso com Google não está configurado nesta instalação. Use email e palavra-passe; a chave (GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET) tem de ser adicionada no servidor.",
    "apple_failed": "Não foi possível iniciar sessão com Apple",
    "contract_docx": "Descarregar o contrato integral (DOCX)",
    "costs_title": "Quanto lhe custa o Hostaly",
    "costs_body": "Sem custo de adesão. A comissão aplica-se a cada reserva e desce com o plano.",
    "to_staff": "Atribuíram-lhe um perfil num alojamento? Crie a conta aqui",
    "host_plans": "Planos para anfitriões",
    "pick_plan": "Escolha o plano de que precisa"
  },
  "dash": {
    "welcome": "Bem-vindo,",
    "overview": "Resumo",
    "property": "Alojamento",
    "rooms": "Quartos",
    "bookings": "Reservas",
    "marketplace": "Rede",
    "groups": "Pedidos de grupo",
    "reviews": "Avaliações",
    "ical": "Sincronização OTA",
    "swaps": "Trocas",
    "breakfast": "Pequeno-almoço",
    "total_bookings": "Reservas totais",
    "revenue": "Receitas",
    "open_offers": "Ofertas abertas",
    "published": "Publicado",
    "not_published": "Não publicado",
    "publish": "Publicar montra",
    "unpublish": "Ocultar montra",
    "view_showcase": "Ver montra pública",
    "copy_link": "Copiar link",
    "copied": "Copiado!",
    "save": "Guardar alterações",
    "saved": "Guardado",
    "name": "Nome",
    "city": "Cidade",
    "region": "Região",
    "address": "Endereço",
    "country": "País",
    "country_help": "Onde fica o imóvel: daqui vêm os identificadores e os prazos que lhe mostramos.",
    "locality": "Concelho / localidade",
    "currency": "Moeda",
    "currency_help": "A moeda do país. Os preços dos quartos e os débitos continuam em euros: o site converte apenas para visualização.",
    "obligations_title": "Obrigações do país",
    "obligations_hint": "O que tem de ser comunicado, a quem e até quando. A lista é a do país do alojamento: mudando de país mudam as obrigações.",
    "obligations_active": "Ativo",
    "obligations_toggle": "Marcar como ativo",
    "obligations_source": "Fonte oficial",
    "obligations_verified": "Verificado em",
    "obligations_error": "Não consigo guardar a obrigação",
    "timezone": "Fuso horário",
    "phone": "Telefone",
    "desc_it": "Descrição (IT)",
    "desc_en": "Descrição (EN)",
    "amenities": "Serviços (separados por vírgula)",
    "hero_image": "Foto principal",
    "photos": "Galeria de fotos",
    "property_type": "Tipo de alojamento",
    "add_room": "Adicionar quarto",
    "room_name": "Nome do quarto",
    "price_night": "Preço/noite (€)",
    "capacity": "Capacidade",
    "photo_url": "URL da foto",
    "publish_offer": "Publicar na rede",
    "no_bookings": "Ainda não há reservas",
    "no_rooms": "Ainda não há quartos. Adicione um para começar.",
    "offer_from": "De",
    "offer_to": "A",
    "post_group_req": "Publicar pedido de grupo",
    "title": "Título",
    "guests": "Hóspedes",
    "budget": "Orçamento/noite (€)",
    "notes_it": "Notas (IT)",
    "notes_en": "Notas (EN)",
    "respond": "Responder",
    "rooms_offered": "Quartos oferecidos",
    "message": "Mensagem",
    "send": "Enviar resposta",
    "responses": "respostas",
    "total": "Total",
    "night": "noite",
    "nights": "noites",
    "upload_photo": "Carregar foto",
    "uploading": "A carregar...",
    "drop_here": "Arraste ou clique para carregar",
    "remove": "Remover",
    "ical_title": "Sincronize as OTAs por iCal",
    "ical_sub": "Importe disponibilidade e reservas do Booking, Airbnb, Expedia, Vrbo.",
    "ical_url": "URL do iCal",
    "ical_source": "Plataforma",
    "ical_import": "Importar",
    "ical_last_sync": "Última sincronização",
    "ical_events": "Eventos importados",
    "calendar_unified": "Calendário unificado",
    "swap_forward": "Encaminhar para parceiro",
    "swap_title": "Troca de quarto",
    "partner_property": "Alojamento parceiro",
    "partner_room": "Quarto do parceiro",
    "commission_10": "Será retida uma comissão de 10%",
    "swap_notes": "Notas para o parceiro",
    "swap_send": "Propor troca",
    "no_swaps": "Ainda não há trocas",
    "breakfast_title": "Pequeno-almoço centralizado",
    "breakfast_sub": "Ofereça ou reserve pequenos-almoços para os hóspedes da rede.",
    "add_breakfast_slot": "Novo turno de pequeno-almoço",
    "slot_date": "Data",
    "slot_time": "Hora",
    "slot_max": "Lugares máximos",
    "slot_price": "Preço/lugar (€)",
    "book_covers": "Reservar lugares",
    "covers": "lugares",
    "book_slot": "Reservar",
    "available": "disponíveis",
    "your_breakfast_bookings": "As suas reservas de pequeno-almoço",
    "role": "Função",
    "date": "Data",
    "amount": "Valor",
    "source": "Origem",
    "event_title": "Título",
    "start": "Início",
    "end": "Fim",
    "no_events": "Nenhum evento — importe um iCal ou receba uma reserva.",
    "welcome_back": "Bem-vindo de volta",
    "toast_uploaded": "Carregado",
    "toast_upload_failed": "Falha no carregamento",
    "toast_error": "Erro",
    "toast_swap_sent": "Troca proposta · hóspede avisado",
    "toast_slot_added": "Turno adicionado",
    "toast_slot_booked": "Reservado",
    "toast_offer_posted": "Oferta publicada",
    "toast_gr_posted": "Pedido publicado",
    "toast_response_sent": "Resposta enviada",
    "toast_imported": "{n} eventos importados",
    "select_room": "Selecionar quarto",
    "select_partner": "Selecionar parceiro",
    "select_booking": "Selecionar reserva",
    "no_paid_bookings": "Nenhuma reserva paga",
    "per_cover": "por lugar",
    "outgoing": "a sair",
    "incoming": "a entrar",
    "from_label": "De:",
    "to_label": "Até:",
    "commission_10pct": "Comissão (10%)",
    "analytics": "Análise",
    "revenue_monthly": "Receitas mensais",
    "direct_bookings": "Reservas diretas",
    "swap_commissions": "Comissões de trocas",
    "breakfast_income": "Receitas de pequeno-almoço",
    "no_analytics": "Ainda sem dados — comece a receber reservas.",
    "stripe_connect": "Pagamentos automáticos",
    "connect_start": "Ativar Stripe Connect",
    "connect_continue": "Concluir o registo",
    "connect_done": "Pagamentos ativos",
    "connect_pending": "Registo em curso",
    "connect_hint": "Receba automaticamente as reservas diretas líquidas da comissão Hostaly (8% Base · 6% Pro · 4,5% Enterprise). As taxas Stripe de processamento são suportadas pela conta ligada.",
    "accept_split": "Aceitar e repartir",
    "select_partners": "Selecionar parceiros",
    "allocated_rooms": "Quartos atribuídos",
    "allocation_confirmed": "Repartição confirmada",
    "push_title": "Notificações do navegador",
    "push_hint": "Receba um aviso no dispositivo mesmo com o separador fechado.",
    "push_enable": "Ativar notificações",
    "push_disable": "Desativar",
    "push_on": "Ativas",
    "push_off": "Desativadas",
    "push_denied": "Bloqueadas pelo navegador",
    "instagram": "Instagram",
    "ig_handle": "Conta Instagram (@)",
    "ig_posts": "Publicações recentes",
    "ig_add": "Adicionar publicação",
    "ig_image": "URL da imagem",
    "ig_caption": "Legenda",
    "ig_link": "Link da publicação",
    "verification": "Verificação",
    "verify_title": "Verificação do alojamento",
    "verify_hint": "Os alojamentos verificados mostram o selo ✓ no mercado e na montra.",
    "verified": "Verificado",
    "pending_verify": "Pendente",
    "not_verified": "Não verificado",
    "submit_verify": "Pedir verificação",
    "vat_number": "NIF/NIPC",
    "business_name": "Designação social",
    "municipality": "Concelho",
    "declaration": "Declaro, sob a minha responsabilidade, que estes dados estão corretos",
    "admin": "Administração",
    "admin_pending": "Pedidos pendentes",
    "approve": "Aprovar",
    "reject": "Rejeitar",
    "admin_note": "Nota interna",
    "ai_copy": "Gerar com IA",
    "ai_copy_title": "Gerar a montra com IA",
    "ai_copy_hint": "Escreva algumas palavras-chave — a IA cria a descrição IT/EN e os serviços.",
    "ai_keywords": "Palavras-chave (separadas por vírgula)",
    "ai_tone": "Tom",
    "ai_generate": "Gerar",
    "ai_apply": "Aplicar à montra",
    "promo_code": "Código de desconto",
    "promo_apply": "Aplicar",
    "promo_valid": "Código válido",
    "promo_saved": "Poupa",
    "trade_nav": "Trade B2B",
    "trade_title": "Venda B2B para operadores e agências",
    "trade_sub": "Compre blocos de quartos diretamente aos alojamentos italianos a tarifas reservadas.",
    "trade_offers": "Ofertas trade",
    "trade_login": "Entrada trade",
    "trade_register": "Registar agência",
    "trade_company": "Nome da agência / operador",
    "trade_vat": "NIF/NIPC",
    "trade_country": "País",
    "trade_publish": "Publicar oferta trade",
    "trade_new_offer": "Nova oferta trade",
    "trade_rooms_available": "Quartos disponíveis",
    "trade_price_per_night": "Preço/quarto/noite (€)",
    "trade_min_nights": "Noites mínimas",
    "trade_buy": "Comprar agora",
    "trade_orders_mine": "As minhas reservas",
    "trade_sold": "vendidos",
    "trade_tab": "Trade",
    "partner_center_tab": "Partner Center",
    "channels_tab": "Canais OTA",
    "b2b_title": "Canal B2B · bedbank",
    "b2b_subtitle": "Compre tarifas líquidas a um bedbank, aplique a sua margem e devolva a comissão ao parceiro.",
    "b2b_suppliers": "Fornecedores",
    "b2b_add_supplier": "Adicionar fornecedor",
    "b2b_mapping": "Mapeamento de hotéis",
    "b2b_rules": "Regras de preço",
    "b2b_simulator": "Simulador",
    "b2b_settlements": "Liquidações",
    "b2b_net": "Tarifa líquida",
    "b2b_nights": "Noites",
    "b2b_markup": "Margem aplicada",
    "b2b_rebate": "Devolução",
    "b2b_msp": "Preço mínimo (MSP)",
    "b2b_gross": "Preço ao público",
    "b2b_charged": "Cobrado agora",
    "b2b_margin": "Margem",
    "b2b_blocked": "Não vendável",
    "b2b_msp_binding": "Preço limitado pelo mínimo do fornecedor",
    "b2b_cashback": "A devolução chega após a reserva",
    "b2b_needed_markup": "Margem necessária",
    "b2b_target_margin": "Margem objetivo",
    "b2b_credentials_missing": "Faltam credenciais",
    "b2b_confirmed_by": "Confirmado por",
    "b2b_auto": "Associado automaticamente",
    "b2b_review": "A confirmar",
    "b2b_no_candidates": "Nenhum candidato compatível",
    "b2b_partners": "Contrapartes",
    "b2b_add_partner": "Adicionar contraparte",
    "b2b_partner_kind": "Tipo",
    "b2b_partner_agency": "Agência",
    "b2b_partner_tour_operator": "Operador turístico",
    "b2b_partner_hotel": "Outro hotel",
    "b2b_partner_vat": "NIF/NIPC",
    "b2b_payment_terms": "Condições de pagamento",
    "b2b_credit_risk": "Risco de incumprimento",
    "b2b_credit_limit": "Limite de crédito",
    "b2b_terms_agency": "Agências e outros hotéis: sem cobrança a consumidores, logo sem pagamento com cartão.",
    "b2b_outstanding": "Por liquidar",
    "b2b_rates": "Tarifário por contraparte",
    "b2b_net_rate": "Tarifa líquida",
    "b2b_public_price": "Preço da montra",
    "b2b_above_public": "Acima do preço da montra",
    "b2b_above_public_hint": "Está a pedir à contraparte mais do que pede a um hóspede que vem da montra. A agência nota e contorna-o."
  },
  "vetrina": {
    "book": "Reservar",
    "from": "desde",
    "per_night": "/ noite",
    "rooms": "Quartos",
    "reviews": "Avaliações",
    "contact": "Contacto",
    "leave_review": "Deixar avaliação",
    "guest_name": "O seu nome",
    "rating": "Avaliação",
    "comment": "O seu comentário",
    "submit": "Enviar avaliação",
    "services": "Serviços",
    "check_in": "Check-in",
    "check_out": "Check-out",
    "guest_email": "O seu e-mail",
    "confirm_book": "Confirmar e pagar",
    "total_price": "Total",
    "processing": "A processar...",
    "book_this_room": "Reservar este quarto",
    "promo_code": "Código de desconto",
    "promo_apply": "Aplicar",
    "promo_valid": "Código válido",
    "promo_saved": "Poupa",
    "traveller_nudge": "É viajante?",
    "prefill_login": "Entre para preencher os seus dados →",
    "beds": "Camas",
    "beds_hint": "Preço por cama: {count} camas disponíveis neste dormitório.",
    "phone": "Telefone",
    "phone_optional": "Telefone (opcional)",
    "phone_placeholder": "+351 912 345 678",
    "phone_hint": "O anfitrião só o usará para contactos sobre esta reserva.",
    "charged_in": "Cobrança em {currency} · {amount}",
    "fx_note": "Câmbio BCE: 1 EUR = {rate} {currency} ({date})",
    "fx_stale": "último câmbio disponível",
    "pay_now": "Pagar agora",
    "pay_now_hint": "Cartão · cobrança imediata",
    "pay_at_property": "Pagar no alojamento",
    "pay_at_property_hint": "Cartão em garantia · cobrança apenas em caso de no-show",
    "pay_at_hotel": "Pagar no hotel",
    "pay_at_hotel_hint": "Pague tudo no check-in · é preciso um cartão em garantia",
    "pay_at_hotel_title": "Pague tudo no check-in, no alojamento",
    "pay_at_hotel_step1": "Nada é cobrado agora: paga a estadia completa no check-in, diretamente no alojamento.",
    "pay_at_hotel_step2": "É preciso um cartão de crédito válido para garantir a reserva. Não é cobrado hoje: guardamo-lo apenas como garantia.",
    "pay_at_hotel_step3": "O cartão só é cobrado em caso de no-show ou de cancelamento fora de prazo, segundo a política do alojamento.",
    "pay_at_hotel_total": "Total a pagar no alojamento",
    "pay_at_hotel_continue": "Continuar com o cartão em garantia",
    "pay_at_hotel_unavailable": "A garantia com cartão não está disponível neste momento: reserve com pagamento por cartão ou contacte o alojamento.",
    "paypal_hint": "Pague com a sua conta PayPal",
    "pay_with_paypal": "Pagar com PayPal",
    "paypal_note": "Será redirecionado para o PayPal para concluir o pagamento em segurança e depois volta aqui.",
    "redirecting": "A redirecionar…",
    "enter_email_code": "Introduza email e código",
    "invalid_code": "Código inválido",
    "phone_invalid": "Introduza um número de telefone válido",
    "name_email_required": "Nome e email obrigatórios",
    "pick_dates": "Escolha as datas",
    "accept_policy": "Tem de aceitar a política de no-show para continuar",
    "report_content": "Denunciar conteúdo",
    "guest_terms": "Condições de reserva",
    "free_cancel": "Cancelamento gratuito",
    "no_show_policy": "Política de no-show",
    "penalty": "Penalização",
    "free_cancel_note": "Pode cancelar gratuitamente até {days} dias antes do check-in.",
    "free_cancel_note_one": "Pode cancelar gratuitamente até 1 dia antes do check-in.",
    "by_date": "Até {date}.",
    "then_retained": "Depois, são retidos {percent}%.",
    "penalty_note": "Cancelamento sujeito a penalização: {penalty}.",
    "first_night": "primeira noite",
    "percent_of_total": "{percent}% do total",
    "guarantee_card": "Cartão em garantia",
    "no_charge_note": "Nenhuma cobrança agora. Fazemos apenas uma verificação temporária de 1 € (liberada de imediato) para validar o cartão.",
    "no_show_consent": "Aceito que, em caso de no-show após o prazo, seja cobrado o valor de {amount} no cartão indicado.",
    "confirm_no_charge": "Confirmar reserva (sem cobrança)",
    "please_wait": "Aguarde…",
    "booking_confirmed": "Reserva confirmada · Cartão •••• {last4} guardado em garantia",
    "verified": "Verificado",
    "guest_favourite": "Favorito dos hóspedes",
    "guest_favourite_hint": "Favorito dos hóspedes — atribuído aos alojamentos mais apreciados",
    "rates_loading": "A carregar câmbios...",
    "per_bed_night": "por cama / noite",
    "dorm_badge": "Dormitório {gender} · {beds} camas",
    "floor": "Piso {number}",
    "free_cancel_badge": "Cancelamento gratuito até {days} dias antes",
    "guest": "Hóspede",
    "thanks": "Obrigado!",
    "posting_as": "Publica como",
    "rating_5": "Perfeito",
    "rating_4": "Bom",
    "rating_3": "Razoável",
    "rating_2": "Insuficiente",
    "rating_1": "Muito mau",
    "dictate_review": "Ditar a avaliação",
    "gender_mixed": "misto",
    "gender_female": "feminino",
    "gender_male": "masculino"
  },
  "guest": {
    "my_bookings": "As minhas reservas",
    "my_maps": "Os meus mapas",
    "travellers_area": "Área de viajantes",
    "have_account": "Já tem conta?",
    "login": "Entrar",
    "signup": "Registar"
  },
  "pay": {
    "success": "Reserva confirmada",
    "success_body": "Receberá um e-mail de confirmação. Obrigado!",
    "cancel": "Pagamento cancelado",
    "cancel_body": "Não foi efetuado qualquer débito.",
    "back_home": "Voltar ao início",
    "checking": "A verificar o pagamento..."
  },
  "common": {
    "close": "Fechar",
    "cancel": "Cancelar",
    "loading": "A carregar...",
    "city": "Cidade",
    "filter_city": "Filtrar por cidade"
  },
  "trip_group": {
    "propose_title": "Outros hóspedes nas suas datas",
    "propose_caption": "Existe um grupo de viajantes para {from} → {until}. Pode juntar-se, mesmo só para conversar: sem obrigação de viajar juntos.",
    "joined_title": "Está no grupo",
    "joined_caption": "O chat do grupo está aberto durante as datas em comum.",
    "join": "Juntar-me",
    "more": "Como funciona",
    "consent_note": "Ao juntar-se ativa o consentimento para grupos de viajantes. É genérico e revogável: sem obrigação de viajar juntos.",
    "consent_enable": "Ativar participação",
    "optin_badge": "Viajantes sozinhos",
    "optin_title": "Viaja sozinho? Há quem viva as suas mesmas experiências.",
    "optin_caption": "Nas suas datas há quem visite, coma e caminhe onde vai: junte-se ao grupo e partilhe apenas o que lhe apetecer.",
    "optin_body": "A adesão só se confirma no momento da viagem e pode ser retirada quando quiser, sem consequências para a reserva.",
    "optin_on": "Adesão ativa",
    "optin_off": "Não adiro",
    "optin_saved": "Preferência guardada. Pode alterá-la quando quiser.",
    "optin_login": "Inicie sessão como viajante para a ativar",
    "optin_error": "Não consigo guardar a preferência.",
    "login_required": "Entre para se juntar ao grupo.",
    "join_failed": "Não consigo adicioná-lo ao grupo.",
    "cap_reached": "Já está em 2 grupos sobrepostos: saia de um para entrar neste.",
    "tooltip_title": "Grupos de viajantes",
    "tooltip_1": "Agrupa-se por local e período: basta um dia de estadia em comum.",
    "tooltip_2": "Pode estar em vários grupos, mas no máximo dois sobrepõem-se nas mesmas datas.",
    "tooltip_3": "O chat está aberto apenas durante as datas em comum: as mensagens fora dessas datas não chegam.",
    "tooltip_4": "Os outros membros veem apenas um nome curto, nunca os detalhes da sua reserva.",
    "tooltip_5": "Se cancelar a reserva sai do grupo; se ficar sozinho, o grupo fecha.",
    "tooltip_window": "Período",
    "loading": "A carregar...",
    "back": "Voltar",
    "chat_title": "Chat do grupo",
    "chat_hint": "Só os membros com datas em comum veem estas mensagens. Nenhum detalhe da reserva é partilhado.",
    "chat_failed": "Não consigo carregar o chat.",
    "send_failed": "Mensagem não enviada.",
    "chat_empty": "Escreva uma mensagem.",
    "no_access": "Sem acesso",
    "no_access_detail": "O chat é reservado aos membros com uma estadia em comum e está aberto apenas durante as datas partilhadas.",
    "no_access_after_consent": "O consentimento está ativo, mas não faz parte deste grupo: as datas já não coincidem ou o grupo fechou.",
    "not_found": "Grupo não encontrado",
    "no_messages": "Ainda não há mensagens. Quebre o gelo.",
    "new_message": "Nova mensagem",
    "message_placeholder": "Escreva algo ao grupo...",
    "send": "Enviar",
    "cancel": "Cancelar",
    "announce": "Aviso",
    "announcement": "Aviso",
    "meeting": "Encontro",
    "meeting_proposal": "Proposta de encontro",
    "new_meeting": "Propor um encontro",
    "meeting_when": "Quando",
    "meeting_when_placeholder": "p. ex. sábado de manhã",
    "meeting_when_required": "Indique quando.",
    "meeting_where": "Onde",
    "meeting_where_placeholder": "p. ex. a entrada do museu",
    "meeting_where_required": "Indique onde.",
    "meeting_note": "Nota (opcional)",
    "meeting_note_placeholder": "Algo a acrescentar?",
    "propose": "Propor",
    "members": "Membros",
    "member_count": "Membros: {n}",
    "no_members": "Ainda não há membros.",
    "anonymous": "Hóspede",
    "chat_ok": "No chat",
    "chat_no": "Só grupo"
  }
};
